<template>
  <SideBar />

  <main id="sidenavmain" class="main-content position-relative max-height-vh-100 h-100 border-radius-lg">
    <NavBar />

    <div class="py-4 container-fluid mb-4">
      <div class="col-lg-12 position-relative mt-n3">

        <div class="ts-head d-flex align-items-center gap-2 mb-3">
          <h6 class="ts-title mb-0">Academics settings</h6>
        </div>

        <div v-if="isLoading" class="card ts-card">
          <div class="ts-card-body">
            <div v-for="n in 6" :key="n" class="row g-3 mb-2">
              <div class="col-md-6">
                <div class="ts-skel" style="width:120px;height:10px;margin-bottom:6px"></div>
                <div class="ts-skel" style="height:38px"></div>
              </div>
              <div class="col-md-6">
                <div class="ts-skel" style="width:120px;height:10px;margin-bottom:6px"></div>
                <div class="ts-skel" style="height:38px"></div>
              </div>
            </div>
          </div>
        </div>

        <form v-else @submit.prevent="save">

          <!-- ═══ Academic year & exam config ═══ -->
          <div class="card ts-card p-3 mb-3">
            <div class="ts-card-body">
              <div class="mb-3">
                <h6 class="mb-0">Academic year &amp; exams</h6>
                <p class="text-xs text-muted mb-0">
                  Core academic configuration for the current year.
                </p>
              </div>

              <div class="row g-3">
                <div class="col-md-6">
                  <label class="tw-label">Current academic year</label>
                  <input v-model="form.academicyr" type="text" class="tw-input" maxlength="300" placeholder="e.g. 2026" />
                </div>
                <div class="col-md-6">
                  <label class="tw-label">Final exam label</label>
                  <input v-model="form.finalexamdef" type="text" class="tw-input" maxlength="500" placeholder="e.g. KCSE, KCPE, A-Levels" />
                  <p class="text-xs text-muted mb-0 mt-1">
                    What your school considers a "final" exam for graduation purposes.
                  </p>
                </div>

                <div class="col-md-6">
                  <label class="tw-label">Subjects for average (school-wide default)</label>
                  <input v-model.number="form.subjectsforavg" type="number" min="0" max="30" class="tw-input" />
                  <p class="text-xs text-muted mb-0 mt-1">
                    Number of subjects used to compute a student's mean. Individual
                    classes can override this on the Classes page.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- ═══ Subject selection ═══ -->
          <div class="card ts-card p-3 mb-3">
            <div class="ts-card-body">
              <div class="mb-3">
                <h6 class="mb-0">Subject selection</h6>
                <p class="text-xs text-muted mb-0">
                  When students choose their elective subjects.
                </p>
              </div>

              <div class="row g-3">
                <div class="col-md-6">
                  <label class="tw-label">Selection class</label>
                  <select v-model.number="form.subselectionclass" class="tw-input">
                    <option :value="0">— No selection phase —</option>
                    <option v-for="c in classOptions" :key="c.id" :value="c.id">{{ c.name }}</option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="tw-label">Selection term</label>
                  <input v-model.number="form.subselectionterm" type="number" min="0" max="99" class="tw-input" />
                  <p class="text-xs text-muted mb-0 mt-1">
                    Term number (1, 2, 3...) when selection happens. 0 if not used.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- ═══ Report form behaviour ═══ -->
          <div class="card ts-card p-3 mb-3">
            <div class="ts-card-body">
              <div class="mb-3">
                <h6 class="mb-0">Report form behaviour</h6>
                <p class="text-xs text-muted mb-0">
                  How report cards are generated and what they show.
                </p>
              </div>

              <div class="row g-3">
                <div class="col-12">
                  <div class="as-toggle-row">
                    <div>
                      <p class="fw-bold mb-0">Include fee balances on reports</p>
                      <p class="text-xs text-muted mb-0">
                        Students' fee balances appear alongside their marks on the report card.
                      </p>
                    </div>
                    <div class="form-check form-switch ms-auto">
                      <input v-model="form.includeBalsInReportForm" class="form-check-input" type="checkbox" />
                    </div>
                  </div>
                </div>

                <div class="col-12">
                  <div class="as-toggle-row">
                    <div>
                      <p class="fw-bold mb-0">Show positions in exams</p>
                      <p class="text-xs text-muted mb-0">
                        Print each student's class / stream / overall position on the report.
                      </p>
                    </div>
                    <div class="form-check form-switch ms-auto">
                      <input v-model="form.showPositionsInExams" class="form-check-input" type="checkbox" />
                    </div>
                  </div>
                </div>

                <div class="col-12">
                  <div class="as-toggle-row">
                    <div>
                      <p class="fw-bold mb-0">Class teachers enter marks</p>
                      <p class="text-xs text-muted mb-0">
                        When enabled, class teachers can enter marks for any subject in their class —
                        not only subjects they personally teach.
                      </p>
                    </div>
                    <div class="form-check form-switch ms-auto">
                      <input v-model="form.useClassTeacherToEnterMarks" class="form-check-input" type="checkbox" />
                    </div>
                  </div>
                </div>

                <div class="col-12">
                  <div class="as-toggle-row">
                    <div>
                      <p class="fw-bold mb-0">Allow exams on mobile</p>
                      <p class="text-xs text-muted mb-0">
                        Students can access exam portal features from mobile devices.
                      </p>
                    </div>
                    <div class="form-check form-switch ms-auto">
                      <input v-model="form.allowExamsOnMobile" class="form-check-input" type="checkbox" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ═══ Report header text ═══ -->
          <div class="card ts-card p-3 mb-3">
            <div class="ts-card-body">
              <div class="mb-3">
                <h6 class="mb-0">Report header text</h6>
                <p class="text-xs text-muted mb-0">
                  Short headings printed on receipts and report cards.
                </p>
              </div>

              <div class="row g-3">
                <div class="col-md-6">
                  <label class="tw-label">Fee receipt text</label>
                  <input v-model="form.schoolreceipttext" type="text" class="tw-input" maxlength="100" placeholder="e.g. For headteacher" />
                </div>
                <div class="col-md-6">
                  <label class="tw-label">Report card text</label>
                  <input v-model="form.schoolassesmenttext" type="text" class="tw-input" maxlength="100" placeholder="e.g. For headteacher" />
                </div>
              </div>
            </div>
          </div>

          <!-- ═══ Exam-result SMS template ═══ -->
          <div id="exam-template" class="card ts-card p-3 mb-3">
            <div class="ts-card-body">
              <div class="mb-3 d-flex align-items-start" style="gap:12px;">
                <div style="flex:1;">
                  <h6 class="mb-0">Exam-result SMS template</h6>
                  <p class="text-xs text-muted mb-0">
                    The message parents receive when you send exam results.
                    Use placeholders like <code>:mceename:</code> — they're replaced with real data per student.
                  </p>
                </div>
                <span v-if="examTemplateLoaded" class="et-badge"
                      :class="examTemplateIsCustom ? 'et-badge-custom' : 'et-badge-default'">
                  {{ examTemplateIsCustom ? 'Custom' : 'Default' }}
                </span>
              </div>

              <div class="et-preview-wrap">
                <div v-if="!examTemplateLoaded" class="ts-skel" style="height:64px"></div>
                <div v-else class="et-preview">{{ examTemplatePreview || 'No template set.' }}</div>
              </div>

              <div class="d-flex justify-content-end mt-2" style="gap:8px;">
                <button type="button" class="tw-btn tw-btn-ghost" @click="openExamTemplateEditor">
                  <span class="material-icons text-sm" style="vertical-align:middle">edit</span>
                  Edit template
                </button>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end">
            <button type="submit" class="tw-btn tw-btn-primary" :disabled="isSaving">
              {{ isSaving ? 'Saving...' : 'Save changes' }}
            </button>
          </div>

        </form>

        <!-- ═══ Exam-template editor modal ═══ -->
        <div class="et-overlay" v-if="showExamEditor" @click.self="closeExamTemplateEditor">
          <div class="et-modal">
            <div class="et-modal-head">
              <h6 class="et-modal-title">Edit exam-result SMS template</h6>
              <button class="et-close" @click="closeExamTemplateEditor">
                <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div class="et-modal-body">
              <div class="et-layout">
                <!-- Variables sidebar -->
                <div class="et-vars">
                  <div class="et-vars-title">Variables</div>
                  <input v-model="examVarSearch" class="et-vars-search" placeholder="Search..." />
                  <div class="et-vars-list">
                    <button v-for="ph in filteredExamPlaceholders" :key="ph.value" type="button"
                            class="et-var-item" @click="insertExamVariable(ph)">
                      <span class="et-var-label">{{ ph.label }}</span>
                      <svg width="12" height="12" fill="none" stroke="var(--theme-primary)" stroke-width="2" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
                    </button>
                    <div v-if="filteredExamPlaceholders.length === 0" class="et-vars-empty">No variables match</div>
                  </div>
                </div>

                <!-- Editor area -->
                <div class="et-editor-area">
                  <div class="et-toolbar">
                    <button type="button" class="et-tool-btn" :class="{ active: examEditor && examEditor.isActive('bold') }" @click="examEditor.chain().focus().toggleBold().run()">B</button>
                    <button type="button" class="et-tool-btn" :class="{ active: examEditor && examEditor.isActive('italic') }" @click="examEditor.chain().focus().toggleItalic().run()" style="font-style:italic">I</button>
                    <button type="button" class="et-tool-btn" @click="clearExamEditor">Clear</button>
                    <button type="button" class="et-tool-btn et-tool-reset" @click="resetExamTemplate">Reset to default</button>
                  </div>
                  <div class="et-editor-content" @click="focusExamEditor">
                    <editor-content :editor="examEditor" />
                  </div>
                  <div class="et-editor-hint">Click a variable to insert it as a chip. Variables are replaced with real data when sending.</div>
                </div>
              </div>
            </div>

            <div class="et-modal-foot">
              <button type="button" class="tw-btn tw-btn-ghost" @click="closeExamTemplateEditor">Cancel</button>
              <button type="button" class="tw-btn tw-btn-primary" @click="saveExamTemplate" :disabled="examTemplateSaving">
                {{ examTemplateSaving ? 'Saving...' : 'Save changes' }}
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
    <AppFooter />
  </main>

</template>

<script>
/* eslint-disable */
import SideBar from "@/fragments/exam/AcademicsSidebar.vue";
import NavBar from "@/fragments/exam/AcademicsNavBar.vue";
import AppFooter from "@/fragments/Footer.vue";
import apiService from '@/apis/apiService';
import swal from 'sweetalert';
import { Editor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import { Node, mergeAttributes } from "@tiptap/core";

const ExamVariableNode = Node.create({
  name: "examVariable", group: "inline", inline: true, atom: true,
  addAttributes() { return { id: { default: null }, label: { default: null } }; },
  parseHTML() { return [{ tag: "span[data-variable]" }]; },
  renderHTML({ node, HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes, {
      "data-variable": node.attrs.id,
      class: "et-variable-chip",
      contenteditable: "false",
    }), node.attrs.label];
  },
});

const EXAM_PLACEHOLDERS = [
  { label: "Student Name",    value: ":mceename:" },
  { label: "Admission No",    value: ":mceeadm:" },
  { label: "Class / Stream",  value: ":mceeclass:" },
  { label: "Exam Name",       value: ":mceeexamname:" },
  { label: "Exam Category",   value: ":mceeexamcategory:" },
  { label: "Term Name",       value: ":mceeterm:" },
  { label: "Academic Year",   value: ":mceeyear:" },
  { label: "Mean Grade",      value: ":mceegrade:" },
  { label: "Mean Mark",       value: ":mceemean:" },
  { label: "Mean Out Of",     value: ":mceemeanof:" },
  { label: "Total Marks",     value: ":mceetotal:" },
  { label: "Total Out Of",    value: ":mceetotalof:" },
  { label: "Position",        value: ":mceeposition:" },
  { label: "Position Out Of", value: ":mceepositionof:" },
  { label: "Subject Scores",  value: ":mceesubjects:" },
  { label: "Deviation",       value: ":mceedeviation:" },
  { label: "School Name",     value: ":mceeschool:" },
];

const SAMPLE_VALUES = {
  ":mceename:": "Jane Doe",
  ":mceeadm:": "1234",
  ":mceeclass:": "Form 4",
  ":mceeexamname:": "Term 1 ENDTERM",
  ":mceeexamcategory:": "ENDTERM",
  ":mceeterm:": "Term 1",
  ":mceeyear:": String(new Date().getFullYear()),
  ":mceegrade:": "A",
  ":mceemean:": "85.5",
  ":mceemeanof:": "100",
  ":mceetotal:": "513",
  ":mceetotalof:": "600",
  ":mceeposition:": "3",
  ":mceepositionof:": "120",
  ":mceesubjects:": "ENG:85 A, MAT:92 A, KIS:78 B+",
  ":mceedeviation:": "+5",
  ":mceeschool:": "Sample School",
};

export default {
  name: "AcademicsSettingsPage",
  components: { SideBar, NavBar, AppFooter, EditorContent },

  data() {
    return {
      isLoading: true,
      isSaving: false,

      classOptions: [],

      form: {
        academicyr: '',
        finalexamdef: '',
        subselectionclass: 0,
        subselectionterm: 0,
        subjectsforavg: 0,
        useClassTeacherToEnterMarks: false,
        allowExamsOnMobile: true,
        showPositionsInExams: true,
        includeBalsInReportForm: false,
        schoolreceipttext: '',
        schoolassesmenttext: '',
      },

      // ── Exam-result SMS template editor ──
      examTemplateRaw: '',
      examTemplateDefault: '',
      examTemplateIsCustom: false,
      examTemplateLoaded: false,
      showExamEditor: false,
      examEditor: null,
      examVarSearch: '',
      examTemplateSaving: false,
      examPlaceholders: EXAM_PLACEHOLDERS,
    };
  },

  computed: {
    examTemplatePreview() {
      let t = this.examTemplateRaw || this.examTemplateDefault || '';
      Object.entries(SAMPLE_VALUES).forEach(([ph, v]) => { t = t.split(ph).join(v); });
      return t.trim();
    },
    filteredExamPlaceholders() {
      if (!this.examVarSearch) return this.examPlaceholders;
      const q = this.examVarSearch.toLowerCase();
      return this.examPlaceholders.filter(p => p.label.toLowerCase().includes(q));
    },
  },

  mounted() {
    this.loadClasses();
    this.fetchSettings();
    this.loadExamTemplate();
    if (window.innerWidth >= 1024) {
      import('perfect-scrollbar').then((m) => {
        const PS = m.default;
        const c = document.getElementById('sidenavmain');
        if (c) new PS(c);
      });
    }
    // Deep-link from sendexamresults.vue: scroll to the template card
    this.$nextTick(() => {
      if (this.$route.hash === '#exam-template') {
        setTimeout(() => {
          const el = document.getElementById('exam-template');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 400);
      }
    });
  },

  beforeUnmount() { if (this.examEditor) this.examEditor.destroy(); },

  methods: {
    async loadClasses() {
      try {
        const res = await apiService.getDataDynamic('/api/settings/loadclasses');
        const rows = Array.isArray(res) ? res : [];
        this.classOptions = rows
          .filter(r => r && (r.no || r.id))
          .map(r => ({ id: r.no || r.id, name: r.Class || r.class || '' }));
      } catch (e) { /* ignore */ }
    },

    async fetchSettings() {
      this.isLoading = true;
      try {
        const res = await apiService.apiClient.get('/api/academics/settings');
        Object.assign(this.form, res.data || {});
      } catch (e) {
        swal("Error", this.readErr(e, "Could not load settings"), "error");
      } finally {
        this.isLoading = false;
      }
    },

    async save() {
      this.isSaving = true;
      try {
        const res = await apiService.apiClient.post('/api/academics/settings', this.form);
        swal("Saved", (res.data && res.data.querystatus) || "Saved", "success");
      } catch (e) {
        swal("Error", this.readErr(e, "Could not save settings"), "error");
      } finally {
        this.isSaving = false;
      }
    },

    readErr(e, fb) { return (e?.response?.data?.querystatus) || fb; },

    // ───────────── Exam-result SMS template ─────────────
    async loadExamTemplate() {
      try {
        const res = await apiService.apiClient.get('/api/v1/exams/notifications/templates/EXAM_RESULTS_SMS');
        const d = res.data || {};
        this.examTemplateRaw     = d.template || '';
        this.examTemplateDefault = d.defaultTemplate || d.template || '';
        this.examTemplateIsCustom = !!d.isCustom;
      } catch (e) { /* leave defaults blank — preview will say "No template set" */ }
      finally { this.examTemplateLoaded = true; }
    },

    initExamEditor() {
      if (this.examEditor) return;
      this.examEditor = new Editor({
        extensions: [StarterKit, ExamVariableNode],
        content: '',
        editorProps: { attributes: { class: 'prose prose-sm focus:outline-none' } },
      });
    },

    openExamTemplateEditor() {
      this.initExamEditor();
      this.examVarSearch = '';
      this.examEditor.commands.setContent(
              this.parseRawToEditor(this.examTemplateRaw || this.examTemplateDefault || ''));
      this.showExamEditor = true;
    },
    closeExamTemplateEditor() {
      this.showExamEditor = false;
      if (this.examEditor) this.examEditor.commands.setContent('');
    },
    focusExamEditor() { if (this.examEditor) this.examEditor.chain().focus().run(); },

    insertExamVariable(v) {
      if (!this.examEditor) return;
      this.examEditor.chain().focus().insertContent([
        { type: 'examVariable', attrs: { id: v.value, label: v.label } },
        { type: 'text',         text: ' ' },
      ]).run();
    },

    parseRawToEditor(text) {
      if (!text) return '';
      const lines = String(text).replace(/\\n/g, '\n').split('\n');
      return {
        type: 'doc',
        content: lines.map(line => {
          const parts   = line.split(/(:mcee[a-z]+:)/g);
          const content = parts.filter(p => p !== '').map(part => {
            const ph = this.examPlaceholders.find(p => p.value === part);
            if (ph) return { type: 'examVariable', attrs: { id: ph.value, label: ph.label } };
            return { type: 'text', text: part };
          });
          return content.length === 0 ? { type: 'paragraph' } : { type: 'paragraph', content };
        }),
      };
    },

    extractTemplateText() {
      if (!this.examEditor) return '';
      const json = this.examEditor.getJSON();
      if (!json.content) return '';
      return json.content.map(block => {
        if (!block.content) return '';
        return block.content.map(node =>
          node.type === 'examVariable' ? node.attrs.id : (node.text || '')
        ).join('');
      }).join('\n').trim();
    },

    async saveExamTemplate() {
      this.examTemplateSaving = true;
      try {
        const plain = this.extractTemplateText();
        await apiService.apiClient.post('/api/v1/exams/notifications/templates', {
          type: 'EXAM_RESULTS_SMS',
          template: plain,
        });
        this.examTemplateRaw     = plain;
        this.examTemplateIsCustom = !!plain;
        this.closeExamTemplateEditor();
        swal('Saved', 'Exam-result SMS template updated.', 'success');
      } catch (e) {
        swal('Error', e?.response?.data?.error || 'Failed to save template.', 'error');
      } finally {
        this.examTemplateSaving = false;
      }
    },

    clearExamEditor() {
      if (this.examEditor) {
        this.examEditor.commands.clearContent();
        this.examEditor.commands.focus();
      }
    },

    async resetExamTemplate() {
      try {
        await swal({
          title: 'Reset to default?',
          text: 'This will remove your custom template and use the system default.',
          icon: 'warning',
          buttons: ['Cancel', 'Reset'],
          dangerMode: true,
        });
      } catch (e) { return; }
      this.examTemplateSaving = true;
      try {
        await apiService.apiClient.post('/api/v1/exams/notifications/templates', {
          type: 'EXAM_RESULTS_SMS',
          template: '__RESET_DEFAULT__',
        });
        await this.loadExamTemplate();
        this.closeExamTemplateEditor();
      } catch (e) {
        swal('Error', e?.response?.data?.error || 'Failed to reset template.', 'error');
      } finally {
        this.examTemplateSaving = false;
      }
    },
  },
};
</script>

<style scoped src="@/assets/css/ts-table.css"></style>

<style scoped>
.as-toggle-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fafafa;
  border: 1px solid rgba(0, 0, 0, 0.05);
}
.as-toggle-row .form-check-input {
  width: 2.2em;
  height: 1.2em;
  cursor: pointer;
}

/* ── Exam template card ── */
.et-badge {
  font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 10px;
  text-transform: uppercase; letter-spacing: .03em; align-self: flex-start;
}
.et-badge-custom { background: var(--theme-success-bg); color: var(--theme-success); }
.et-badge-default { background: #f1f5f9; color: #64748b; }

.et-preview-wrap { padding: 10px 0; }
.et-preview {
  font-size: 12px; color: #475569; padding: 12px 14px; background: #f8fafc;
  border: 1px solid #e2e8f0; border-radius: 8px; line-height: 1.6;
  white-space: pre-wrap;
}

/* ── Editor modal ── */
.et-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.4);
  z-index: 10000; display: flex; align-items: center; justify-content: center;
  padding: 16px; backdrop-filter: blur(2px);
}
.et-modal {
  background: #fff; border-radius: 16px; width: 100%; max-width: 860px;
  max-height: 90vh; display: flex; flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
  animation: et-up .2s ease;
}
@keyframes et-up { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
.et-modal-head {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 22px 14px; border-bottom: 1px solid #f1f5f9;
}
.et-modal-title { font-size: 15px; font-weight: 700; color: #1e293b; margin: 0; }
.et-close { background: none; border: none; cursor: pointer; color: #94a3b8; padding: 4px; border-radius: 6px; }
.et-close:hover { background: #f1f5f9; color: #475569; }
.et-modal-body { flex: 1; overflow-y: auto; padding: 0; }
.et-modal-foot { display: flex; justify-content: flex-end; gap: 8px; padding: 14px 22px; border-top: 1px solid #f1f5f9; }

.et-layout { display: flex; min-height: 380px; }
@media (max-width: 768px) { .et-layout { flex-direction: column; } }

.et-vars { width: 200px; border-right: 1px solid #f1f5f9; padding: 14px; flex-shrink: 0; background: #fafbfc; }
@media (max-width: 768px) { .et-vars { width: 100%; border-right: none; border-bottom: 1px solid #f1f5f9; max-height: 180px; overflow-y: auto; } }
.et-vars-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #94a3b8; margin-bottom: 8px; }
.et-vars-search { width: 100%; padding: 6px 8px; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 12px; margin-bottom: 8px; font-family: inherit; }
.et-vars-search:focus { outline: none; border-color: var(--theme-primary); }
.et-vars-list { display: flex; flex-direction: column; gap: 3px; max-height: 300px; overflow-y: auto; }
.et-var-item { display: flex; justify-content: space-between; align-items: center; padding: 6px 8px; border: none; background: none; border-radius: 6px; cursor: pointer; transition: background .15s; font-family: inherit; text-align: left; }
.et-var-item:hover { background: var(--theme-primary-bg); }
.et-var-label { font-size: 12px; font-weight: 500; color: #475569; }
.et-vars-empty { font-size: 12px; color: #94a3b8; text-align: center; padding: 12px 0; }

.et-editor-area { flex: 1; display: flex; flex-direction: column; padding: 14px 18px; }
.et-toolbar { display: flex; gap: 4px; margin-bottom: 8px; }
.et-tool-btn {
  padding: 4px 10px; border: 1px solid #e2e8f0; border-radius: 6px;
  background: #fff; font-size: 12px; font-weight: 600; cursor: pointer;
  transition: all .15s; font-family: inherit; color: #475569;
}
.et-tool-btn:hover { background: #f1f5f9; }
.et-tool-btn.active { background: var(--theme-primary-bg); border-color: var(--theme-primary); color: var(--theme-primary); }
.et-tool-reset { color: var(--theme-danger); border-color: var(--theme-danger-soft); }
.et-tool-reset:hover { background: var(--theme-danger-bg-soft); border-color: var(--theme-danger); }

.et-editor-content {
  flex: 1; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px;
  min-height: 220px; cursor: text; transition: border-color .15s;
  overflow-y: auto;
}
.et-editor-content:focus-within {
  border-color: var(--theme-primary); box-shadow: 0 0 0 3px rgba(var(--theme-primary-rgb), 0.08);
}
.et-editor-hint { font-size: 11px; color: #94a3b8; margin-top: 6px; }

:deep(.ProseMirror) { outline: none; min-height: 220px; }
:deep(.ProseMirror p) { margin-bottom: .4em; font-size: 13px; line-height: 1.6; color: #344767; }
:deep(.et-variable-chip) {
  display: inline-flex; align-items: center; background: var(--theme-primary-bg); color: var(--theme-primary-dark);
  border: 1px solid var(--theme-primary-soft); border-radius: 4px; padding: 0 6px; margin: 0 2px;
  font-size: 11px; font-weight: 700; white-space: nowrap; user-select: none;
  vertical-align: middle; cursor: default;
}
:deep(.ProseMirror-selectednode) { outline: 2px solid var(--theme-primary); }

/* Dark mode */
body.dark-version .et-preview { background: #0f172a; border-color: #334155; color: #cbd5e1; }
body.dark-version .et-modal { background: #1e293b; }
body.dark-version .et-modal-head, body.dark-version .et-modal-foot { border-color: #334155; }
body.dark-version .et-modal-title { color: #e2e8f0; }
body.dark-version .et-vars { background: #0f172a; border-color: #334155; }
body.dark-version .et-vars-search { background: #1e293b; border-color: #334155; color: #e2e8f0; }
body.dark-version .et-var-item:hover { background: #334155; }
body.dark-version .et-var-label { color: #cbd5e1; }
body.dark-version .et-editor-content { background: #0f172a; border-color: #334155; }
body.dark-version .et-tool-btn { background: #1e293b; border-color: #334155; color: #cbd5e1; }
body.dark-version :deep(.ProseMirror p) { color: #cbd5e1; }
body.dark-version :deep(.et-variable-chip) { background: var(--theme-primary-deep-dark); color: var(--theme-primary-on-dark); border-color: var(--theme-primary-deep); }
</style>
